import {ReactElement, useState, useEffect, useMemo} from "react";
import * as css from "./styles/unitTypeManagement.module.scss";
import {ListUnitTypesQuery} from "../../../../__generated__/generated_types";
import {Checkbox, Field, MediaInput, Label, Message} from '@zendeskgarden/react-forms';
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {Inline} from "@zendeskgarden/react-loaders";
import {UnitTypePMSInfo} from "./types";
import {Cell, Head, HeaderRow, SortableCell, HeaderCell, Body as TBody, Row, Table} from "@zendeskgarden/react-tables";
import {nextSortType, SortType} from "../../../../utils/sorting";
import {ReactComponent as SearchIcon} from '@zendeskgarden/svg-icons/src/16/search-stroke.svg';
import {ReactComponent as CheckIcon} from '@zendeskgarden/svg-icons/src/16/check-lg-fill.svg';

type UnitTypePMSInfoForEdit = Pick<UnitTypePMSInfo, "name" | "seenEarliest"> & {
    isUsedAlready: 0 | 1;
};

export type EditUnitTypeProps = {
    existingUnitTypes: ListUnitTypesQuery["listUnitTypes"];
    pmsUnitTypes: Pick<UnitTypePMSInfo, "name" | "seenEarliest">[] | undefined,
    pmsUnitTypesAsOfDate: string | undefined,
    initialName?: string;
    unitTypeId?: string;
    title: string;
    onCancel: () => void;
    onSubmit: (names: string[]) => void;
};

type SortColumn = "NAME" | "SEEN_EARLIEST" | "USED_ALREADY";
type SortField = keyof Pick<UnitTypePMSInfoForEdit, "name" | "seenEarliest" | "isUsedAlready">;

export function EditUnitType(props: EditUnitTypeProps): ReactElement {
    const isAddMode = props.unitTypeId === undefined;
    const hasPmsUnitTypes = (props.pmsUnitTypes?.length ?? 0) > 0;
    const [name, setName] = useState<string>(props.initialName ?? "");
    const [valid, setValid] = useState({valid: true, error: ""});
    const [inAlert, setInAlert] = useState(false);
    const [applyClicked, setApplyClicked] = useState(false);
    const [searchString, setSearchString] = useState("");
    const [hideAlreadyAdded, setHideAlreadyAdded] = useState(true);
    const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());
    const [pmsUnitTypes, setPMSUnitTypes] = useState<UnitTypePMSInfoForEdit[]>([]);

    useEffect(() => {
        if(!props.pmsUnitTypes || props.pmsUnitTypes.length === 0) {
            setPMSUnitTypes([]);
            return;
        }
        const next: UnitTypePMSInfoForEdit[] = props.pmsUnitTypes.map(pmsUnitType => ({
            name: pmsUnitType.name,
            seenEarliest: pmsUnitType.seenEarliest,
            isUsedAlready: props.existingUnitTypes.find(eut => eut.name === pmsUnitType.name) ? 1 : 0
        }));
        setPMSUnitTypes(next);
        setSelectedNames(new Set());
    }, [props.pmsUnitTypes, props.existingUnitTypes]);

    useEffect(() => {
        let nextValid = true;
        let error = "";
        let nextInAlert = false;
        const trimmed = name.trim();

        if(trimmed.length > 0 && trimmed !== (props.initialName ?? "")) {
            const sameFound = props.existingUnitTypes.find(unitType =>
                unitType.id !== props.unitTypeId
                && unitType.name === trimmed
            );
            nextValid = sameFound === undefined;
            if(!nextValid) {
                error = "Same Unit Type Exists";
            }
        }

        if(props.pmsUnitTypes && props.pmsUnitTypes.length > 0 && trimmed.length > 0) {
            nextInAlert = !props.pmsUnitTypes.find(item => item.name === trimmed);
        }

        setInAlert(nextInAlert);
        setValid({valid: nextValid, error});
    }, [name, props.existingUnitTypes, props.initialName, props.pmsUnitTypes, props.unitTypeId]);

    function handleSortUpdate(column: SortColumn | undefined) {
        if (column !== sortColumn) {
            setSortColumn(column);
            setSortType(nextSortType(undefined));
        }
        else {
            setSortType(nextSortType(sortType));
        }
    }

    function currentSortForColumn(column: SortColumn | undefined): SortType | undefined {
        let type: SortType | undefined = undefined;
        if (!column) {
            type = undefined;
        }
        else if (sortColumn === column) {
            type = sortType;
        }

        return type;
    }

    const [sortColumn, setSortColumn] = useState<SortColumn>();
    const [sortType, setSortType] = useState<SortType>();

    const visibleRows = useMemo(() => {
        let updated = [...pmsUnitTypes];
        if(isAddMode && hideAlreadyAdded) {
            updated = updated.filter(item => item.isUsedAlready === 0);
        }
        if(searchString.trim().length > 0) {
            const needle = searchString.toLowerCase();
            updated = updated.filter(item => item.name.toLowerCase().includes(needle));
        }

        let sortField: SortField | undefined = undefined;
        let sortAscending: boolean | undefined = undefined;
        if(sortColumn && sortType) {
            if(sortColumn === "NAME") {
                sortField = "name";
            } else if(sortColumn === "SEEN_EARLIEST") {
                sortField = "seenEarliest";
            } else if(sortColumn === "USED_ALREADY") {
                sortField = "isUsedAlready";
            }
            if(sortType === "asc") {
                sortAscending = true;
            } else if(sortType === "desc") {
                sortAscending = false;
            }
        }

        if(sortField !== undefined && sortAscending !== undefined) {
            updated = sortAscending ? updated.sortBy(sortField) : updated.sortByDescending(sortField);
        } else {
            updated = updated.sortBy("name");
        }
        return updated;
    }, [pmsUnitTypes, searchString, hideAlreadyAdded, sortColumn, sortType, isAddMode]);

    const selectableVisibleNames = useMemo(
        () => visibleRows.filter(row => row.isUsedAlready === 0).map(row => row.name),
        [visibleRows]
    );

    const allVisibleSelected = selectableVisibleNames.length > 0
        && selectableVisibleNames.every(n => selectedNames.has(n));

    function toggleName(unitTypeName: string, checked: boolean) {
        setSelectedNames(prev => {
            const next = new Set(prev);
            if(checked) {
                next.add(unitTypeName);
            } else {
                next.delete(unitTypeName);
            }
            return next;
        });
    }

    function toggleSelectAllVisible(checked: boolean) {
        setSelectedNames(prev => {
            const next = new Set(prev);
            for(const unitTypeName of selectableVisibleNames) {
                if(checked) {
                    next.add(unitTypeName);
                } else {
                    next.delete(unitTypeName);
                }
            }
            return next;
        });
    }

    function namesToSubmit(): string[] {
        if(!isAddMode) {
            const trimmed = name.trim();
            return trimmed.length > 0 ? [trimmed] : [];
        }
        const names = Array.from(selectedNames);
        const trimmed = name.trim();
        if(trimmed.length > 0 && valid.valid && !names.includes(trimmed)) {
            names.push(trimmed);
        }
        return names;
    }

    function canSubmit(): boolean {
        if(applyClicked || !valid.valid) {
            return false;
        }
        if(!isAddMode) {
            const trimmed = name.trim();
            return trimmed.length > 0 && trimmed !== (props.initialName ?? "");
        }
        return namesToSubmit().length > 0;
    }

    function handleSubmit() {
        const names = namesToSubmit();
        if(names.length === 0) {
            return;
        }
        setApplyClicked(true);
        props.onSubmit(names);
    }

    const modalTitle = isAddMode && !hasPmsUnitTypes ? "Add Unit Type" : props.title;

    return (
        <Modal onClose={props.onCancel} isLarge style={{minWidth: "80rem", overflow: "unset"}}>
            <Header>
                {modalTitle}
            </Header>
            <Body>
                <form className={css.editForm}>
                    <Field>
                        <Label>
                            {isAddMode && hasPmsUnitTypes ? "Custom Unit Type Name (optional)" : "Unit Type Name"}
                        </Label>
                        <MediaInput value={name} onChange={e => setName(e.target.value)} />
                    </Field>
                </form>
                {hasPmsUnitTypes &&
                    <>
                        <Label style={{paddingTop: "1rem"}}>
                            Known Unit Type Names
                            {props.pmsUnitTypesAsOfDate &&
                                <span>&nbsp;(as of {props.pmsUnitTypesAsOfDate})</span>
                            }
                        </Label>
                        <div className={css.pmsPanel}>
                            <div className={css.pmsTableFilters}>
                            <MediaInput start={<SearchIcon />} value={searchString} placeholder="Search" onChange={e => setSearchString(e.target.value)} />
                                {isAddMode &&
                                    <Field>
                                        <Checkbox
                                            checked={hideAlreadyAdded}
                                            onChange={e => setHideAlreadyAdded(e.target.checked)}
                                        >
                                            <Label>Hide already added</Label>
                                        </Checkbox>
                                    </Field>
                                }
                            </div>
                            <div className={css.pmsTableWrapper}>
                                <Table className={css.pmsTable}>
                                    <Head isSticky className={css.pmsTableHead}>
                                        <HeaderRow>
                                            {isAddMode &&
                                                <HeaderCell className={css.pmsSelectCell}>
                                                    <Field>
                                                        <Checkbox
                                                            checked={allVisibleSelected}
                                                            disabled={selectableVisibleNames.length === 0}
                                                            onChange={e => toggleSelectAllVisible(e.target.checked)}
                                                        >
                                                            <Label className={css.pmsSelectCheckboxLabel} />
                                                        </Checkbox>
                                                    </Field>
                                                </HeaderCell>
                                            }
                                            <SortableCell
                                                onClick={() => handleSortUpdate("NAME")}
                                                sort={currentSortForColumn("NAME")}
                                            >
                                                Unit Type Name
                                            </SortableCell>
                                            <SortableCell
                                                onClick={() => handleSortUpdate("SEEN_EARLIEST")}
                                                sort={currentSortForColumn("SEEN_EARLIEST")}
                                            >
                                                First Seen in PMS
                                            </SortableCell>
                                            <SortableCell
                                                onClick={() => handleSortUpdate("USED_ALREADY")}
                                                sort={currentSortForColumn("USED_ALREADY")}
                                            >
                                                {isAddMode ? "Already Added" : "Used Already"}
                                            </SortableCell>
                                            {!isAddMode && <HeaderCell />}
                                        </HeaderRow>
                                    </Head>
                                    <TBody>
                                        {visibleRows.map(ut =>
                                            <Row key={ut.name}>
                                                {isAddMode &&
                                                    <Cell className={css.pmsSelectCell}>
                                                        <Field>
                                                            <Checkbox
                                                                checked={selectedNames.has(ut.name)}
                                                                disabled={ut.isUsedAlready === 1}
                                                                onChange={e => toggleName(ut.name, e.target.checked)}
                                                            >
                                                                <Label className={css.pmsSelectCheckboxLabel} />
                                                            </Checkbox>
                                                        </Field>
                                                    </Cell>
                                                }
                                                <Cell>{ut.name}</Cell>
                                                <Cell>{ut.seenEarliest}</Cell>
                                                <Cell>{ut.isUsedAlready ? <CheckIcon /> : null}</Cell>
                                                {!isAddMode &&
                                                    <Cell>
                                                        <Button isBasic onClick={() => setName(ut.name)}>Use</Button>
                                                    </Cell>
                                                }
                                            </Row>
                                        )}
                                    </TBody>
                                </Table>
                            </div>
                        </div>
                    </>
                }
            </Body>
            <Footer>
                <FooterItem>
                    {!valid.valid ?
                        <Message validation="error">{valid.error}</Message>
                        : inAlert && !isAddMode && hasPmsUnitTypes ?
                            <Message validation="warning">Unit Type not found in PMS</Message>
                            : isAddMode && hasPmsUnitTypes ?
                                <span>{selectedNames.size} selected from PMS</span>
                                : null
                    }
                </FooterItem>
                <FooterItem>
                    <Button onClick={props.onCancel} isBasic>
                        Cancel
                    </Button>
                </FooterItem>
                <FooterItem>
                    <Button
                        disabled={!canSubmit()}
                        onClick={handleSubmit}
                    >
                        {
                            applyClicked
                                ? <Inline size={24} aria-label="loading" />
                                : isAddMode && hasPmsUnitTypes
                                    ? <span>Add ({namesToSubmit().length})</span>
                                    : isAddMode
                                        ? <span>Add</span>
                                        : <span>Apply</span>
                        }
                    </Button>
                </FooterItem>
            </Footer>
            <Close aria-label="Close modal" />
        </Modal>
    );
}

